// detailWorkbenchModel — the pure half of the library source detail: which
// tabs the reader offers and what its one quiet facts line says. No React, no
// copy (the view localizes each case), so both are unit-testable.

import type { LoadedSource } from "../../app/ports/resource.js";

// The reader's tabs: the file first (the protagonist), then one tab per
// section that HAS content. An empty section gets no tab at all.
export type ReaderTab = "file" | "summary" | "notes" | "usedIn";

export type ReaderTabEntry = {
    readonly id: ReaderTab;
    // How many items the tab holds (notes, materials); absent ⇒ no count.
    readonly count?: number;
};

export const readerTabsOf = (
    source: Pick<LoadedSource, "summary" | "annotations" | "derivedMaterials">,
): readonly ReaderTabEntry[] => [
    { id: "file" },
    ...(source.summary !== undefined && source.summary.length > 0
        ? [{ id: "summary" } satisfies ReaderTabEntry]
        : []),
    ...(source.annotations.length > 0
        ? [
              {
                  id: "notes",
                  count: source.annotations.length,
              } satisfies ReaderTabEntry,
          ]
        : []),
    ...(source.derivedMaterials.length > 0
        ? [
              {
                  id: "usedIn",
                  count: source.derivedMaterials.length,
              } satisfies ReaderTabEntry,
          ]
        : []),
];

// One fact on the header's quiet line, joined with " · ". Who wrote the work
// leads when known; otherwise the resource kind ("Source") stands in its place.
export type SourceFact =
    | { readonly type: "byline"; readonly text: string }
    | { readonly type: "kind" }
    | { readonly type: "format"; readonly text: string }
    | { readonly type: "added"; readonly date: Date };

export const sourceFactsOf = ({
    byline,
    format,
    addedOnIso,
}: {
    readonly byline: string | undefined;
    readonly format: string;
    readonly addedOnIso: string | undefined;
}): readonly SourceFact[] => {
    const added = addedOnIso === undefined ? undefined : new Date(addedOnIso);
    return [
        byline !== undefined && byline !== ""
            ? { type: "byline", text: byline }
            : { type: "kind" },
        ...(format !== ""
            ? [{ type: "format", text: format } satisfies SourceFact]
            : []),
        ...(added !== undefined && !Number.isNaN(added.getTime())
            ? [{ type: "added", date: added } satisfies SourceFact]
            : []),
    ];
};
